import { useCallback, useEffect, useRef, useState, type ChangeEvent } from "react";
import { resizeImage } from "../lib/resizeImage";

/**
 * Explicit types instead of image/*: with the wildcard, Android Chrome offers (or jumps to) the
 * camera; a list of types opens the gallery / photo picker.
 */
export const GALLERY_ACCEPT = "image/jpeg,image/png,image/webp,image/heic,image/heif";

export function cameraErrorMessage(err: unknown): string {
  const name = typeof err === "object" && err !== null && "name" in err ? String((err as { name?: unknown }).name) : "";
  switch (name) {
    case "NotAllowedError": return "Permiso de cámara denegado. Aceptalo en el navegador e intentá de nuevo.";
    case "NotFoundError": return "No se encontró una cámara en este dispositivo.";
    case "NotReadableError": return "La cámara está siendo usada por otra aplicación.";
    default: return "No se pudo abrir la cámara. Probá subir una foto desde la galería.";
  }
}

export interface PhotoPickerLabels {
  camera: string;
  gallery: string;
  shoot: string;
  cancel: string;
  starting: string;
}

const DEFAULT_LABELS: PhotoPickerLabels = {
  camera: "Abrir cámara",
  gallery: "Subir foto",
  shoot: "Sacar foto",
  cancel: "Cancelar",
  starting: "Abriendo…",
};

interface Props {
  /** Receives the photo already downscaled (JPEG, max 2000px) when possible. */
  onPhoto: (photo: Blob) => void;
  onError?: (message: string) => void;
  labels?: Partial<PhotoPickerLabels>;
}

/** Live camera (getUserMedia, rear camera) or a saved photo from the gallery. */
export function PhotoPicker({ onPhoto, onError, labels }: Props) {
  const l = { ...DEFAULT_LABELS, ...labels };
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const [cameraOn, setCameraOn] = useState(false);
  const [starting, setStarting] = useState(false);

  const stopCamera = useCallback(() => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    setCameraOn(false);
    setStarting(false);
  }, []);

  // The <video> only exists once cameraOn renders it: attach the stream then.
  // Android Chrome needs an explicit play() even with autoPlay + playsInline.
  useEffect(() => {
    if (!cameraOn) return;
    const video = videoRef.current;
    if (!video) return;
    video.srcObject = streamRef.current;
    video.play().catch(() => {});
    return () => {
      video.srcObject = null;
    };
  }, [cameraOn]);

  useEffect(() => stopCamera, [stopCamera]);

  function startCamera() {
    if (!navigator.mediaDevices?.getUserMedia) {
      onError?.("Este navegador no permite usar la cámara acá. Subí una foto desde la galería.");
      return;
    }
    setStarting(true);
    navigator.mediaDevices
      .getUserMedia({ video: { facingMode: "environment", width: { ideal: 1920 }, height: { ideal: 1080 } } })
      .then((s) => {
        streamRef.current = s;
        setCameraOn(true);
        setStarting(false);
      })
      .catch((err) => {
        onError?.(cameraErrorMessage(err));
        setStarting(false);
      });
  }

  function capture() {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas) return;
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext("2d")!.drawImage(video, 0, 0);
    canvas.toBlob(
      (blob) => {
        if (!blob) return;
        stopCamera();
        void resizeImage(blob).then(onPhoto);
      },
      "image/jpeg",
      0.9,
    );
  }

  function onFileSelected(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = ""; // picking the same file again must fire onChange
    if (file) void resizeImage(file).then(onPhoto);
  }

  if (cameraOn) {
    return (
      <div className="identify-viewfinder">
        <video ref={videoRef} autoPlay playsInline muted className="identify-video" />
        <canvas ref={canvasRef} hidden />
        <div className="identify-viewfinder-actions">
          <button type="button" className="btn btn-primary" onClick={capture}>
            📸 {l.shoot}
          </button>
          <button type="button" className="btn" onClick={stopCamera}>
            {l.cancel}
          </button>
        </div>
      </div>
    );
  }
  return (
    <div className="identify-actions">
      <button type="button" className="btn btn-primary" onClick={startCamera} disabled={starting}>
        {starting ? l.starting : `📷 ${l.camera}`}
      </button>
      {/* A label, not input.click(): Safari ignores programmatic clicks on hidden file inputs. */}
      <label className="btn" role="button" tabIndex={0}>
        🖼️ {l.gallery}
        <input type="file" accept={GALLERY_ACCEPT} onChange={onFileSelected} hidden />
      </label>
    </div>
  );
}
